import { makeAutoObservable, observable, runInAction, action, computed } from "mobx";
import { RootStore } from "./rootStore";
import { PaymentDtoV2, SubscriptionDtoV3 } from "../models/dtoTypes";
import { fetchSubscriptionsV3 } from "../api/subscriptionService";
import UserStore from "./userStore";

//what a signed-in customer must be told about, worst first (design D14):
//a failed renewal payment, a lapsed licence, or a licence ending soon without auto-renewal
export type AttentionKind = 'pastDue' | 'expired' | 'expiring';

export interface SubscriptionAttention {
    kind: AttentionKind;
    subscription: SubscriptionDtoV3;
    //days until the licence ends (negative once lapsed); NaN when the date can't be parsed
    daysLeft: number;
}

//the desktop app's reminder windows (RenewAlertViewModel): monthly 5 days, annual 10
const EXPIRING_WINDOW_DAYS = (billingPeriodMonths?: number) => billingPeriodMonths === 1 ? 5 : 10;
//a lapse is worth nagging about for this long (the server's expired-reminder cadence stops
//at 20 days); an old subscription the customer replaced years ago is history, not a problem
const EXPIRED_ATTENTION_DAYS = 30;

const daysUntil = (endDate: string | undefined | null): number => {
    if (!endDate) return NaN;
    const end = new Date(endDate);
    if (isNaN(end.getTime())) return NaN;
    return Math.floor((end.getTime() - Date.now()) / 86400000);
};

export default class SubscriptionsStore {
    rootStore: RootStore | undefined;
    userStore: UserStore | undefined;
    @observable subscriptionsV3: SubscriptionDtoV3[] = [];
    @observable loading: boolean = false;
    @observable error: string | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.userStore = rootStore.userStore;
        makeAutoObservable(this);
    }

    @action fetchSubscriptionsV3() {
        if (this.loading) return;
        this.loading = true;
        fetchSubscriptionsV3().then((subscriptionsV3: SubscriptionDtoV3[]) => {
            runInAction(() => {
                this.subscriptionsV3 = subscriptionsV3;
                this.loading = false;
                this.error = null;
            });
        }).catch((error) => {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
        });
    }

    @action clear() {
        this.subscriptionsV3 = [];
        this.error = null;
    }

    //one attention state for the whole account, from the server-enriched list only:
    //past-due beats expired beats expiring; blocked subscriptions and healthy auto-renewing
    //ones never count, and "expiring" only inside the plan's reminder window
    @computed get attention(): SubscriptionAttention | null {
        const subs = this.subscriptionsV3.filter(s => !s.isBlocked);

        const pastDue = subs.find(s => s.billingStatus === 'past_due');
        if (pastDue) return { kind: 'pastDue', subscription: pastDue, daysLeft: daysUntil(pastDue.endDate) };

        for (const s of subs) {
            if (!s.isExpired || !s.renewalLink) continue;
            const days = daysUntil(s.endDate);
            if (!isNaN(days) && days >= -EXPIRED_ATTENTION_DAYS)
                return { kind: 'expired', subscription: s, daysLeft: days };
        }

        for (const s of subs) {
            if (s.isExpired || !s.renewalLink) continue;
            if (s.isRecurring && s.autoRenew !== false) continue; //Stripe renews it by itself
            const days = daysUntil(s.endDate);
            if (!isNaN(days) && days <= EXPIRING_WINDOW_DAYS(s.billingPeriodMonths))
                return { kind: 'expiring', subscription: s, daysLeft: days };
        }
        return null;
    }

    @action openInvoiceModal(payment: PaymentDtoV2) {
        this.rootStore?.invoiceStore.openInvoiceModal(payment.id, payment.invoiceHtml ?? null, payment.countryName ?? null);
    }
}
